/**
 * LTI Integration API Client
 *
 * This module provides two separate API interfaces:
 * 1. ltiManagementApi - For CRUD operations on LTI resources (requires user authentication)
 * 2. ltiLaunchApi - For LTI launch flows (called by LMS, typically unauthenticated)
 *
 * @module lti.api
 */

import axios from 'axios';
import config from '../../config';

// Create separate axios instance for client API (LTI endpoints)
const clientApi = axios.create({
  baseURL: config.clientApiUrl,
  timeout: 30000,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// Response interceptor
clientApi.interceptors.response.use(
    response => {
      // DRF может возвращать data в двух форматах:
      // 1. Прямо объект: { id: 1, name: "test", ... }
      // 2. Обернутый: { data: { id: 1, name: "test", ... } }
      // Проверяем и возвращаем правильные данные
      if (response.data && response.data.data !== undefined) {
        return response.data.data;
      }
      return response.data;
    },
    error => {
      if (error.response?.status === 401) {
        error.message = 'Login required';
      } else if (error.response?.status === 403) {
        error.message = 'Insufficient access rights';
      } else if (error.response?.status >= 500) {
        error.message = 'Server error. Please try again later';
      } else if (!error.response) {
        error.message = 'Connection issues with the server';
      } else if (error.response?.data?.error) {
        error.message = error.response.data.error;
      } else if (error.response?.data?.detail) {
        error.message = error.response.data.detail;
      }
      return Promise.reject(error);
    }
);

/**
 * LTI Management API
 * Used by authenticated users (instructors) to manage LTI resources.
 * Requires user authentication token.
 */
export const ltiManagementApi = {
  // Assignment Links
  createAssignmentLink: (assignmentId, data, token) =>
    clientApi.post('/lti/links/', {
      assignment_id: assignmentId,
      ...data
    }, {
      headers: { Authorization: `Token ${token}` },
    }),

  getAssignmentLinks: (assignmentId, token) =>
    clientApi.get('/lti/links/', {
      params: { assignment: assignmentId },
      headers: { Authorization: `Token ${token}` },
    }),

  getAssignmentLink: (linkId, token) =>
    clientApi.get(`/lti/links/${linkId}/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  revokeAssignmentLink: (linkId, token) =>
    clientApi.post(`/lti/links/${linkId}/revoke/`, {}, {
      headers: { Authorization: `Token ${token}` },
    }),

  // External Systems
  getExternalSystems: (token) =>
    clientApi.get('/lti/systems/', {
      headers: { Authorization: `Token ${token}` },
    }),

  getExternalSystem: (systemId, token) =>
    clientApi.get(`/lti/systems/${systemId}/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  createExternalSystem: (data, token) =>
    clientApi.post('/lti/systems/', data, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateExternalSystem: (systemId, data, token) =>
    clientApi.patch(`/lti/systems/${systemId}/`, data, {
      headers: { Authorization: `Token ${token}` },
    }),

  deleteExternalSystem: (systemId, token) =>
    clientApi.delete(`/lti/systems/${systemId}/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Grade Passback
  sendGrade: (studentId, assignmentId, score, comment, token) =>
    clientApi.post('/lti/grades/passback/', {
      student_id: studentId,
      assignment_id: assignmentId,
      score,
      comment
    }, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Shadow Profiles
  getShadowProfiles: (token) =>
    clientApi.get('/lti/shadow-profiles/', {
      headers: { Authorization: `Token ${token}` },
    }),

  promoteShadowProfile: (profileId, data, token) =>
    clientApi.post(`/lti/shadow-profiles/${profileId}/promote/`, data, {
      headers: { Authorization: `Token ${token}` },
    }),
};

/**
 * LTI Launch API
 * Used for LTI launch flows and OIDC authentication.
 * These endpoints are typically called by LMS platforms, not directly by frontend.
 *
 * @note These are PUBLIC endpoints that do NOT require user authentication.
 * @note Frontend should redirect to these URLs, not call them via AJAX.
 */
export const ltiLaunchApi = {
  /**
   * Get OIDC login URL
   * @returns {string} Full URL for LTI OIDC login endpoint
   */
  getOidcLoginUrl: () => `${config.clientApiUrl}/lti/oidc-login/`,

  /**
   * Get LTI launch URL
   * @returns {string} Full URL for LTI launch endpoint
   */
  getLaunchUrl: () => `${config.clientApiUrl}/lti/launch/`,

  /**
   * Get JWKS URL (for LMS to verify our signatures)
   * @returns {string} Full URL for JWKS endpoint
   */
  getJwksUrl: () => `${config.clientApiUrl}/lti/jwks/`,

  /**
   * Get Deep Linking URL
   * @returns {string} Full URL for Deep Linking endpoint
   */
  getDeepLinkUrl: () => `${config.clientApiUrl}/lti/deep-link/`,
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use ltiManagementApi instead
 */
export const ltiApi = ltiManagementApi;
